// Admin: edit runtime-mutable settings (env mirror in DB).
// See backend-ts/src/services/settings_service.ts for read precedence
// (DB → env, SETTINGS_FORCE_ENV=true bypasses DB).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { request, printAgentsAdminApi, PDF_SINK_AGENT_ID } from "@/lib/api";

type SettingKind = "string" | "number" | "boolean" | "string-list" | "secret";
// Keep in sync with backend settings_service.ts `group` union. Unknown groups
// coming from the backend are tolerated at runtime (see `grouped` below) so a new
// backend group can never white-screen this page again.
type SettingGroup = "corporate" | "smtp" | "erp" | "auth" | "print" | "manufacturing" | "packing";
type SettingValue = string | number | boolean | string[] | null;

interface SettingItem {
  key: string;
  group: SettingGroup;
  kind: SettingKind;
  label: string;
  hint?: string;
  // For kind='secret' the backend always sends null here (write-only) — the
  // current value is never returned to the client. Use `is_set` instead.
  effective: SettingValue;
  from_db: boolean;
  is_set: boolean;
}

interface ListResponse {
  force_env: boolean;
  items: SettingItem[];
}

// Preferred display order. Any backend group not listed here is appended after
// these (in first-seen order), so the page degrades gracefully instead of crashing.
const GROUP_ORDER: SettingGroup[] = ["corporate", "smtp", "erp", "auth", "print", "manufacturing", "packing"];

export default function SystemSettingsPage() {
  return (
    <RequirePermission codes="user.manage">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "system-settings"],
    queryFn: () => request<ListResponse>("/admin/system-settings"),
  });

  // Build the group map dynamically from whatever the backend returns. Lazily
  // creating buckets means an unknown group (e.g. a new backend `group` value the
  // frontend hasn't been taught yet) is rendered instead of throwing on `.push`.
  const grouped = useMemo(() => {
    const m: Record<string, SettingItem[]> = {};
    for (const it of q.data?.items ?? []) (m[it.group] ??= []).push(it);
    return m;
  }, [q.data]);

  // Ordered list of group keys actually present: preferred order first, then any
  // extras in first-seen order.
  const groupKeys = useMemo(() => {
    const present = Object.keys(grouped);
    const ordered = GROUP_ORDER.filter((g) => present.includes(g));
    const extras = present.filter((g) => !GROUP_ORDER.includes(g as SettingGroup));
    return [...ordered, ...extras];
  }, [grouped]);

  return (
    <>
      <PageHeader
        eyebrow={t("eyebrows.adminSettings")}
        title={t("systemSettings.title")}
        description={t("systemSettings.description")}
      />

      {q.data?.force_env && (
        <div className="card p-3 mt-3 border-warn">
          <p className="text-sm">
            ⚠ <strong>SETTINGS_FORCE_ENV=true</strong> — {t("systemSettings.forceEnvWarning")}
          </p>
        </div>
      )}

      {q.isLoading && <LoadingState />}
      {q.isError && <ErrorState error={q.error} />}

      {q.data &&
        groupKeys.map((g) =>
          grouped[g].length === 0 ? null : (
            <section key={g} className="mt-6">
              <h2 className="text-base text-text-heading mb-3">
                {t(`systemSettings.groups.${g}`, { defaultValue: g })}
              </h2>
              <div className="card divide-y divide-border">
                {grouped[g].map((it) => (
                  <Row
                    key={it.key}
                    item={it}
                    onSaved={() => qc.invalidateQueries({ queryKey: ["admin", "system-settings"] })}
                  />
                ))}
              </div>
            </section>
          ),
        )}
    </>
  );
}

function Row({ item, onSaved }: { item: SettingItem; onSaved: () => void }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string>(serialize(item.effective, item.kind));
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    setDraft(serialize(item.effective, item.kind));
  }, [item.effective, item.kind]);

  const m = useMutation({
    mutationFn: async (value: SettingValue) => {
      await request<void>(`/admin/system-settings/${item.key}`, {
        method: "PATCH",
        body: JSON.stringify({ value }),
      });
    },
    onSuccess: onSaved,
  });

  function save() {
    const parsed = parse(draft, item.kind);
    if (parsed === SENTINEL_INVALID) {
      alert(t("systemSettings.invalidFormat"));
      return;
    }
    m.mutate(parsed);
  }

  function clear() {
    if (!confirm(t("systemSettings.confirmReset", { label: item.label }))) return;
    m.mutate(null);
  }

  const isSecret = item.kind === "secret";
  const inputType = isSecret && !showSecret ? "password" : "text";

  return (
    <div className="p-4 grid grid-cols-1 md:grid-cols-[260px,1fr,auto] gap-3 items-start">
      <div>
        <p className="text-sm font-medium">{item.label}</p>
        <p className="font-mono text-xs text-text-muted mt-0.5">{item.key}</p>
        {item.hint && <p className="text-text-muted text-xs mt-1">{item.hint}</p>}
        <p className="text-xs mt-1">
          {item.from_db ? (
            <span className="pill pill-accent text-[10px]">{t("systemSettings.fromDb")}</span>
          ) : (
            <span className="pill text-[10px]">{t("systemSettings.fromEnv")}</span>
          )}
        </p>
      </div>
      <div className="min-w-0">
        {item.key === "PRINT_DEFAULT_AGENT_ID" ? (
          <AgentSelectField value={draft} onChange={setDraft} />
        ) : item.kind === "boolean" ? (
          <select
            className="w-full px-3 py-2 rounded-md bg-surface border border-border"
            value={draft || "false"}
            onChange={(e) => setDraft(e.target.value)}
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : (
          <input
            type={inputType}
            className="w-full px-3 py-2 rounded-md bg-surface border border-border font-mono text-sm"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={
              isSecret
                ? item.is_set
                  ? t("systemSettings.secretSetPh")
                  : t("systemSettings.secretUnsetPh")
                : item.kind === "string-list"
                  ? t("systemSettings.phCommaSeparated")
                  : item.kind === "number"
                    ? t("systemSettings.phNumber")
                    : ""
            }
          />
        )}
        {isSecret && (
          <div className="mt-1 flex items-center gap-3">
            <span
              className={`pill text-[10px] ${item.is_set ? "pill-accent" : ""}`}
              title={t("systemSettings.secretWriteOnly")}
            >
              {item.is_set ? t("systemSettings.secretSet") : t("systemSettings.secretUnset")}
            </span>
            <button
              type="button"
              className="text-xs text-text-muted hover:text-text-primary"
              onClick={() => setShowSecret((s) => !s)}
            >
              {showSecret ? t("systemSettings.hide") : t("systemSettings.show")}
            </button>
          </div>
        )}
        {m.isError && (
          <div className="mt-2">
            <ErrorBanner onDismiss={() => m.reset()}>{(m.error as Error).message}</ErrorBanner>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <button
          className="btn btn-primary"
          onClick={save}
          disabled={m.isPending || draft === serialize(item.effective, item.kind)}
        >
          {m.isPending ? "…" : t("common.save")}
        </button>
        {item.from_db && (
          <button className="btn" onClick={clear} disabled={m.isPending}>
            {t("common.reset")}
          </button>
        )}
      </div>
    </div>
  );
}

/** Dropdown-селектор принт-агента по умолчанию (ключ PRINT_DEFAULT_AGENT_ID).
 *  Опции: «Сохранить в PDF» (pdf-sink) + все заведённые агенты. Если текущее
 *  значение не найдено среди опций (пусто / неизвестный id) — показываем его
 *  отдельной строкой, чтобы не потерять при сохранении. */
function AgentSelectField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const q = useQuery({
    queryKey: ["admin", "print-agents", "default-select"],
    queryFn: () => printAgentsAdminApi.list(),
  });
  const agents = (q.data?.items ?? []).filter((a) => a.id !== PDF_SINK_AGENT_ID);
  const known = new Set<string>([PDF_SINK_AGENT_ID, ...agents.map((a) => a.id)]);
  return (
    <>
      <select
        className="w-full px-3 py-2 rounded-md bg-surface border border-border"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value={PDF_SINK_AGENT_ID}>📄 Не печатать — сохранить в PDF</option>
        {agents.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
            {!a.is_active ? " (выключен)" : a.last_seen_at ? "" : " · не подключался"}
          </option>
        ))}
        {!known.has(value) && (
          <option value={value}>
            {value === "" ? "— не задано (любой агент) —" : `${value} (неизвестный агент)`}
          </option>
        )}
      </select>
      {q.isError && (
        <p className="text-state-danger text-xs mt-1">не удалось загрузить список агентов</p>
      )}
    </>
  );
}

const SENTINEL_INVALID = Symbol("invalid");

function serialize(v: SettingValue, kind: SettingKind): string {
  if (v === null || v === undefined) return "";
  if (kind === "string-list") return Array.isArray(v) ? v.join(", ") : "";
  if (kind === "boolean") return String(!!v);
  return String(v);
}

function parse(raw: string, kind: SettingKind): SettingValue | typeof SENTINEL_INVALID {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return kind === "boolean" ? false : null;
  }
  if (kind === "number") {
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : SENTINEL_INVALID;
  }
  if (kind === "boolean") {
    return trimmed === "true";
  }
  if (kind === "string-list") {
    return trimmed
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }
  return trimmed;
}
